import { useEffect, useMemo, useRef, useState, type CSSProperties } from "react";
import { Send, X } from "lucide-react";
import { CHAT_MAX_UPLOAD_BYTES } from "@/lib/chatApi";
import {
  bakeChatPhotoFilter,
  CHAT_PHOTO_FILTERS,
  type ChatPhotoFilter,
  type ChatPhotoFilterId,
} from "@/lib/chatPhotoFilters";

type Props = {
  file: File;
  onCancel: () => void;
  onSend: (file: File, caption: string) => void | Promise<boolean | void>;
};

function FilterLook({
  url,
  filter,
  imgClass,
}: {
  url: string;
  filter: ChatPhotoFilter;
  imgClass?: string;
}) {
  return (
    <span className="chat-photo-filter__look">
      <img
        src={url}
        alt=""
        className={imgClass}
        draggable={false}
        style={{ filter: filter.css === "none" ? undefined : filter.css }}
      />
      {filter.overlay ? (
        <span
          className="chat-photo-filter__wash"
          aria-hidden
          style={{
            background: filter.overlay,
            mixBlendMode: (filter.blend ?? "soft-light") as CSSProperties["mixBlendMode"],
          }}
        />
      ) : null}
    </span>
  );
}

export function ChatPhotoFilterSheet({ file, onCancel, onSend }: Props) {
  const captionRef = useRef<HTMLTextAreaElement>(null);
  const [filterId, setFilterId] = useState<ChatPhotoFilterId>("original");
  const [caption, setCaption] = useState("");
  const [busy, setBusy] = useState(false);
  const [chromeOn, setChromeOn] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const url = useMemo(() => URL.createObjectURL(file), [file]);
  const selected = CHAT_PHOTO_FILTERS.find((f) => f.id === filterId) ?? CHAT_PHOTO_FILTERS[0];

  useEffect(() => {
    return () => URL.revokeObjectURL(url);
  }, [url]);

  useEffect(() => {
    setFilterId("original");
    setCaption("");
    setBusy(false);
    setChromeOn(true);
    setErr(null);
  }, [file]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !busy) onCancel();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [busy, onCancel]);

  const send = async () => {
    if (busy) return;
    setBusy(true);
    setErr(null);
    const out = await bakeChatPhotoFilter(file, filterId);
    if (out.size > CHAT_MAX_UPLOAD_BYTES) {
      setErr("Photo trop lourde (max 10 Mo).");
      setBusy(false);
      return;
    }
    const ok = await onSend(out, caption.trim());
    if (ok === false) {
      setBusy(false);
      return;
    }
  };

  return (
    <div
      className={`chat-photo-filter ${chromeOn ? "" : "chat-photo-filter--peek"}`}
      role="dialog"
      aria-modal="true"
      aria-labelledby="chat-photo-filter-title"
    >
      <h2 id="chat-photo-filter-title" className="sr-only">
        Photo
      </h2>
      {chromeOn ? (
        <button
          type="button"
          className="chat-photo-filter__close"
          disabled={busy}
          aria-label="Annuler"
          onClick={onCancel}
        >
          <X className="size-5" />
        </button>
      ) : null}

      <button
        type="button"
        className="chat-photo-filter__stage"
        aria-label={chromeOn ? "Plein écran" : "Afficher les filtres"}
        onClick={() => setChromeOn((v) => !v)}
      >
        <FilterLook url={url} filter={selected} imgClass="chat-photo-filter__preview" />
      </button>

      {chromeOn ? (
        <div className="chat-photo-filter__dock">
          <div className="chat-photo-filter__row" role="listbox" aria-label="Filtres">
            {CHAT_PHOTO_FILTERS.map((f) => {
              const on = f.id === filterId;
              return (
                <button
                  key={f.id}
                  type="button"
                  role="option"
                  aria-selected={on}
                  disabled={busy}
                  className={`chat-photo-filter__swatch ${on ? "chat-photo-filter__swatch--on" : ""}`}
                  onClick={() => setFilterId(f.id)}
                >
                  <span className="chat-photo-filter__swatch-img">
                    <FilterLook url={url} filter={f} />
                  </span>
                  <span className="chat-photo-filter__swatch-label">{f.label}</span>
                </button>
              );
            })}
          </div>
          {err ? <p className="chat-photo-filter__err">{err}</p> : null}
          <div className="chat-photo-filter__compose">
            <textarea
              ref={captionRef}
              value={caption}
              rows={1}
              disabled={busy}
              placeholder="Légende…"
              className="chat-photo-filter__caption"
              onChange={(e) => setCaption(e.target.value)}
              onClick={(e) => e.stopPropagation()}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  void send();
                }
              }}
            />
            <button
              type="button"
              className="chat-photo-filter__send"
              disabled={busy}
              aria-label="Envoyer"
              title="Envoyer"
              onClick={() => void send()}
            >
              <Send className="size-5" />
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
